// ─────────────────────────────────────────────────────────────
// tarifsData.js — Tarifs dynamiques avec taux de change réels
// Les taux USD/EUR viennent de data.tauxChange (API BCE)
// ─────────────────────────────────────────────────────────────

export function getTarifsData(tauxChange = {}) {
  // Taux réels depuis Firebase/BCE — fallback si non disponible
  const fcfaParUsd = tauxChange.fcfaParUsd
    ? Math.round(1 / tauxChange.usdParFcfa)
    : 615;
  const fcfaParEur = tauxChange.fcfaParEur
    ? Math.round(1 / tauxChange.eurParFcfa)
    : 656;

  function toUsd(fcfa)  { return Math.round(fcfa / fcfaParUsd); }
  function toEur(fcfa)  { return Math.round(fcfa / fcfaParEur); }

  return {
    taux: {
      usd: fcfaParUsd,
      eur: fcfaParEur,
      source: tauxChange.source || 'Taux par défaut',
      lastUpdate: tauxChange.lastUpdate || '',
      auto: tauxChange.auto || false,
    },

    saas: {
      title: 'Pack SaaS — Applications Web',
      subtitle: 'Développement et mise à disposition d\'applications web',
      plans: [
        {
          name: 'Starter',
          fcfa: 9000, usd: toUsd(9000), eur: toEur(9000),
          unit: 'utilisateur/mois',
          description: 'Idéal pour les petites structures',
        },
        {
          name: 'Pro',
          fcfa: 18000, usd: toUsd(18000), eur: toEur(18000),
          unit: 'utilisateur/mois',
          description: 'Pour les équipes en croissance',
        },
        {
          name: 'Enterprise',
          fcfa: 30000, usd: toUsd(30000), eur: toEur(30000),
          unit: 'utilisateur/mois',
          description: 'Pour les grandes organisations',
        },
      ],
    },

    data: {
      title: 'Pack Data Premium — Analyse & IA',
      subtitle: 'Tableaux de bord, modèles prédictifs, IA',
      plans: [
        {
          name: 'Starter',
          fcfa: 1200000, usd: toUsd(1200000), eur: toEur(1200000),
          unit: 'projet',
          maintenance: { fcfa: 300000, usd: toUsd(300000), eur: toEur(300000) },
        },
        {
          name: 'Pro',
          fcfa: 3000000, usd: toUsd(3000000), eur: toEur(3000000),
          unit: 'projet',
          maintenance: { fcfa: 300000, usd: toUsd(300000), eur: toEur(300000) },
        },
        {
          name: 'Enterprise',
          fcfa: 6000000, usd: toUsd(6000000), eur: toEur(6000000),
          unit: 'projet',
          maintenance: { fcfa: 300000, usd: toUsd(300000), eur: toEur(300000) },
        },
      ],
    },

    support: {
      title: 'Pack Support & Sécurité',
      subtitle: 'Maintenance applicative, mises à jour, sécurité',
      plans: [
        {
          name: 'Starter',
          fcfa: 300000, usd: toUsd(300000), eur: toEur(300000),
          unit: 'mois',
        },
        {
          name: 'Pro',
          fcfa: 750000, usd: toUsd(750000), eur: toEur(750000),
          unit: 'mois',
        },
        {
          name: 'Enterprise',
          fcfa: 1200000, usd: toUsd(1200000), eur: toEur(1200000),
          unit: 'mois',
        },
      ],
    },

    formation: {
      title: 'Pack Formation & Consulting',
      subtitle: 'Ateliers pratiques, IA, Data Science, Full Stack',
      plans: [
        {
          name: 'Demi-journée',
          fcfa: 115000, usd: toUsd(115000), eur: toEur(115000),
          unit: 'session',
        },
        {
          name: 'Journée',
          fcfa: 230000, usd: toUsd(230000), eur: toEur(230000),
          unit: 'jour',
        },
        {
          name: 'Semaine',
          fcfa: 600000, usd: toUsd(600000), eur: toEur(600000),
          unit: 'semaine',
        },
      ],
    },

    memoires: {
      title: 'Accompagnement Mémoires & Thèses',
      subtitle: 'Appui méthodologique, technique et rédactionnel',
      // Clause de sécurité juridique — non rédactionnel au sens "ghostwriting"
      note: 'KOTOSTECHNOLOGY propose un accompagnement méthodologique, technique et de mise en forme. Nous n\'écrivons pas le mémoire ou la thèse à la place de l\'étudiant : le contenu scientifique et les analyses restent produits par l\'auteur. Toute prestation respecte les règles d\'intégrité académique en vigueur (non-substitution, absence de plagiat).',
      modules: [
        { name: 'Cadrage & problématique',     fcfa: 45000,  usd: toUsd(45000),  eur: toEur(45000)  },
        { name: 'Revue de littérature',        fcfa: 70000,  usd: toUsd(70000),  eur: toEur(70000)  },
        { name: 'Méthodologie & plan',         fcfa: 52500,  usd: toUsd(52500),  eur: toEur(52500)  },
        { name: 'Analyse de données & stats',  fcfa: 120000, usd: toUsd(120000), eur: toEur(120000) },
        { name: 'Développement outil/appli',   fcfa: 180000, usd: toUsd(180000), eur: toEur(180000) },
        { name: 'Relecture & mise en forme',   fcfa: 85000,  usd: toUsd(85000),  eur: toEur(85000)  },
        { name: 'Notes de bas de page',        fcfa: 18000,  usd: toUsd(18000),  eur: toEur(18000)  },
        { name: 'Bibliographie APA/AFNOR',     fcfa: 27000,  usd: toUsd(27000),  eur: toEur(27000)  },
        { name: 'Coaching soutenance',         fcfa: 52500,  usd: toUsd(52500),  eur: toEur(52500)  },
      ],
      forfaits: [
        { name: 'Licence',   fcfa: 210000,  usd: toUsd(210000),  eur: toEur(210000)  },
        { name: 'Master',    fcfa: 405000,  usd: toUsd(405000),  eur: toEur(405000)  },
        { name: 'Doctorat',  fcfa: 1050000, usd: toUsd(1050000), eur: toEur(1050000) },
      ],
    },
  };
}

// ── Taux dynamiques depuis Firebase/BCE ──────────────────────
export function getTaux(tauxChange = {}) {
  return {
    USD: tauxChange.usdParFcfa
      ? Math.round(1 / tauxChange.usdParFcfa)
      : 615,
    EUR: tauxChange.eurParFcfa
      ? Math.round(1 / tauxChange.eurParFcfa)
      : 656,
    source:     tauxChange.source     || 'Taux par défaut',
    lastUpdate: tauxChange.lastUpdate || '',
    auto:       tauxChange.auto       || false,
  };
}

// Export statique par défaut (compatibilité)
export const tarifsData = getTarifsData();

// ── Exports de compatibilité ─────────────────────────────────
const _data = getTarifsData();

export const allTarifs = [
  { id:'saas',       icon:'🖥️',  title:'Pack SaaS — Applications Web',         subtitle:'Développement et mise à disposition d\'applications web', ..._data.saas },
  { id:'data',       icon:'📊',  title:'Pack Data Premium — Analyse & IA',      subtitle:'Tableaux de bord, modèles prédictifs, IA',                ..._data.data },
  { id:'support',    icon:'🛡️',  title:'Pack Support & Sécurité',               subtitle:'Maintenance applicative, mises à jour, sécurité',          ..._data.support },
  { id:'formation',  icon:'📚',  title:'Pack Formation & Consulting',            subtitle:'Ateliers pratiques, IA, Data Science, Full Stack',          ..._data.formation },
  { id:'memoires',   icon:'🎓',  title:'Accompagnement Mémoires & Thèses',      subtitle:'Appui méthodologique, technique et rédactionnel',           ..._data.memoires },
];

// Plateformes — pas de prix propre : le prix dépend du type de projet
// choisi dans les packs ci-dessus. On ne met PAS prixFcfa:0 (0 est
// "truthy-falsy" en JS et s'affiche littéralement en React).
export const plateformes = [
  { id:'web',     icon:'🌐', name:'Web',        desc:'Application web responsive' },
  { id:'mobile',  icon:'📱', name:'Mobile',      desc:'iOS & Android natif ou hybride' },
  { id:'desktop', icon:'🖥️', name:'Desktop',     desc:'Windows, Mac, Linux via Electron' },
  { id:'saas',    icon:'☁️', name:'SaaS Cloud',  desc:'Hébergé et maintenu par KOTOSTECH' },
];

export const tarifsTJM = {
  junior:   { fcfa: 75000,  label: 'Junior Data Scientist / Dev' },
  senior:   { fcfa: 150000, label: 'Senior Data Scientist / Dev' },
  expert:   { fcfa: 250000, label: 'Expert / Architecte' },
  conseil:  { fcfa: 200000, label: 'Consulting stratégique' },
};

export const reglesOr = [
  'Acompte de 30% à la commande',
  'Solde à la livraison',
  'Paiement Mobile Money, virement ou espèces',
  'Devis gratuit sous 24h',
  'Confidentialité garantie (NDA disponible)',
  'Support inclus selon la formule choisie',
];

export const TAUX = getTaux();

export function fmtRange(min, max, devise, taux = TAUX) {
  const T = taux || TAUX;
  if (devise === 'USD') {
    return `${Math.round(min/T.USD).toLocaleString('fr-FR')} – ${Math.round(max/T.USD).toLocaleString('fr-FR')} $`;
  }
  if (devise === 'EUR') {
    return `${Math.round(min/T.EUR).toLocaleString('fr-FR')} – ${Math.round(max/T.EUR).toLocaleString('fr-FR')} €`;
  }
  return `${min.toLocaleString('fr-FR')} – ${max.toLocaleString('fr-FR')} FCFA`;
}